/**
 * BLRRD Transform — Float32 RGBA resamplers.
 *
 * Phase 4.D.transform.1 (Wave 11.1).
 *
 * Three resamplers, all operating on planar interleaved Float32 RGBA
 * buffers with mirror-extend edge handling:
 *
 *   • sampleBilinear  — 2×2 footprint, linear weights. Cheap. Used
 *                       for preview tier + identity-aligned dst.
 *   • sampleBicubic   — 4×4 footprint, Catmull-Rom cubic. Sharper than
 *                       bilinear, no ringing on smooth gradients.
 *   • sampleLanczos3  — 6×6 footprint, sinc · sinc window with a=3.
 *                       Highest quality; mild ringing on hard edges
 *                       but preserves frequency content best.
 *
 * References (clean-room, MIT — no GIMP/RT/dt source consulted):
 *   • Duchon, C. E. (1979), "Lanczos Filtering in One and Two
 *     Dimensions", J. Applied Meteorology 18 (8), 1016–1022.
 *     The canonical paper for the windowed sinc kernel used here.
 *   • Mitchell, D. P. & Netravali, A. N. (1988), "Reconstruction
 *     Filters in Computer Graphics", SIGGRAPH '88 — discusses
 *     Catmull-Rom as a B=0, C=0.5 Mitchell filter.
 *   • Wolberg, G. (1990), "Digital Image Warping", IEEE Computer
 *     Society Press — covers mirror-extend edge handling (§5.3).
 *
 * License: MIT (BLRRD project).
 *
 * Edge handling: all three resamplers mirror-extend the source so
 * sampling at (-0.5, y) returns the same value as (0.5, y). Mirror
 * is reflection without repetition of the edge sample — index -1 maps
 * to 1, index w maps to w-2 (Wolberg 1990 §5.3, "symmetric extension").
 *
 * Output: each sample writes 4 floats to `dst[dstOffset..dstOffset+4)`.
 */

/** Number of channels per pixel (RGBA, fixed). */
const C = 4;

/**
 * Mirror-clamp an integer coordinate into [0, n).
 *
 * Pattern (Wolberg "symmetric extension"):
 *   ... 2 1 0 1 2 3 ... n-2 n-1 n-2 n-3 ...
 *
 * Equivalent to triangle-wave indexing.
 *
 * @param {number} i integer source index
 * @param {number} n source dimension (must be > 0)
 * @returns {number}
 */
export function mirrorIndex(i, n) {
    if (n <= 1) return 0;
    const period = 2 * (n - 1);
    let k = i % period;
    if (k < 0) k += period;
    return k < n ? k : period - k;
}

/**
 * Bilinear sampler — 2×2 footprint.
 *
 * @param {Float32Array} rgba interleaved source RGBA, length w*h*4
 * @param {number} w
 * @param {number} h
 * @param {number} x destination-derived source x (may be fractional/negative)
 * @param {number} y destination-derived source y
 * @param {Float32Array|number[]} dst
 * @param {number} dstOffset
 */
export function sampleBilinear(rgba, w, h, x, y, dst, dstOffset) {
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = x - x0;
    const fy = y - y0;

    const ix0 = mirrorIndex(x0,     w);
    const ix1 = mirrorIndex(x0 + 1, w);
    const iy0 = mirrorIndex(y0,     h);
    const iy1 = mirrorIndex(y0 + 1, h);

    const w00 = (1 - fx) * (1 - fy);
    const w10 =      fx  * (1 - fy);
    const w01 = (1 - fx) *      fy;
    const w11 =      fx  *      fy;

    const o00 = (iy0 * w + ix0) * C;
    const o10 = (iy0 * w + ix1) * C;
    const o01 = (iy1 * w + ix0) * C;
    const o11 = (iy1 * w + ix1) * C;

    for (let c = 0; c < C; c++) {
        dst[dstOffset + c] =
            rgba[o00 + c] * w00
          + rgba[o10 + c] * w10
          + rgba[o01 + c] * w01
          + rgba[o11 + c] * w11;
    }
}

/* Catmull-Rom 1D kernel value at offset `t` ∈ [-2, 2].
 * Mitchell-Netravali with B=0, C=0.5 — standard Catmull-Rom. */
function catmullRomWeight(t) {
    const a = Math.abs(t);
    const a2 = a * a;
    const a3 = a2 * a;
    if (a < 1)      return 1.5 * a3 - 2.5 * a2 + 1;
    if (a < 2)      return -0.5 * a3 + 2.5 * a2 - 4 * a + 2;
    return 0;
}

/**
 * Bicubic (Catmull-Rom) sampler — 4×4 footprint.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} x
 * @param {number} y
 * @param {Float32Array|number[]} dst
 * @param {number} dstOffset
 */
export function sampleBicubic(rgba, w, h, x, y, dst, dstOffset) {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = x - xi;
    const fy = y - yi;

    /* Precompute weights once per axis. */
    const wx0 = catmullRomWeight(-1 - fx + 0);
    const wx1 = catmullRomWeight( 0 - fx + 0);
    const wx2 = catmullRomWeight( 1 - fx + 0);
    const wx3 = catmullRomWeight( 2 - fx + 0);
    const wy0 = catmullRomWeight(-1 - fy + 0);
    const wy1 = catmullRomWeight( 0 - fy + 0);
    const wy2 = catmullRomWeight( 1 - fy + 0);
    const wy3 = catmullRomWeight( 2 - fy + 0);

    /* Mirror indices for the 4-tap window in each axis. */
    const ix = [
        mirrorIndex(xi - 1, w),
        mirrorIndex(xi,     w),
        mirrorIndex(xi + 1, w),
        mirrorIndex(xi + 2, w),
    ];
    const iy = [
        mirrorIndex(yi - 1, h),
        mirrorIndex(yi,     h),
        mirrorIndex(yi + 1, h),
        mirrorIndex(yi + 2, h),
    ];

    const wx = [wx0, wx1, wx2, wx3];
    const wy = [wy0, wy1, wy2, wy3];

    /* 4×4 convolution per channel. */
    let r = 0, g = 0, b = 0, a = 0;
    for (let j = 0; j < 4; j++) {
        const rowOff = iy[j] * w * C;
        const wyJ = wy[j];
        for (let i = 0; i < 4; i++) {
            const off = rowOff + ix[i] * C;
            const wxi = wx[i];
            const wt  = wxi * wyJ;
            r += rgba[off]     * wt;
            g += rgba[off + 1] * wt;
            b += rgba[off + 2] * wt;
            a += rgba[off + 3] * wt;
        }
    }
    dst[dstOffset]     = r;
    dst[dstOffset + 1] = g;
    dst[dstOffset + 2] = b;
    dst[dstOffset + 3] = a;
}

/* Lanczos-3 window — Duchon 1979.
 *   L(x) = sinc(x) · sinc(x/a)  for |x| < a, else 0.
 * The bare sinc has L(0)=1 (handled as the limit). */
const LANCZOS_A = 3;
function lanczosWeight(t) {
    if (t === 0) return 1;
    const at = Math.abs(t);
    if (at >= LANCZOS_A) return 0;
    const pix = Math.PI * t;
    const pixA = pix / LANCZOS_A;
    return (Math.sin(pix) * Math.sin(pixA)) / (pix * pixA);
}

/**
 * Lanczos-3 sampler — 6×6 footprint.
 *
 * Implementation note: the bare 6-tap Lanczos kernel for a given
 * fractional offset sums to ~0.99–1.01 (not exactly 1 — Duchon's
 * paper proves unity-summing only in the limit). Without normalisation
 * a constant-1 source darkens by ~1% at fractional samples, which is
 * visible as a DC shift on flat regions. We therefore **normalise the
 * weights by the per-axis sums** (separable, so the 2D normalisation
 * is `sumWx · sumWy`). This keeps the kernel DC-preserving — verified
 * by `test:transform` assertion `[4]` on a constant-1 source at
 * fractional positions.
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} x
 * @param {number} y
 * @param {Float32Array|number[]} dst
 * @param {number} dstOffset
 */
export function sampleLanczos3(rgba, w, h, x, y, dst, dstOffset) {
    const xi = Math.floor(x);
    const yi = Math.floor(y);
    const fx = x - xi;
    const fy = y - yi;

    /* 6-tap weights in each axis: taps at xi-2, xi-1, xi, xi+1, xi+2, xi+3 */
    const wx = new Array(6);
    const wy = new Array(6);
    let sumWx = 0, sumWy = 0;
    for (let k = 0; k < 6; k++) {
        wx[k] = lanczosWeight((k - 2) - fx);
        wy[k] = lanczosWeight((k - 2) - fy);
        sumWx += wx[k];
        sumWy += wy[k];
    }
    /* Normalise to enforce DC-preservation. Guard against the
     * degenerate sumW≈0 case (can't happen for Lanczos-3 with a≥1
     * but defensive). */
    if (sumWx !== 0) for (let k = 0; k < 6; k++) wx[k] /= sumWx;
    if (sumWy !== 0) for (let k = 0; k < 6; k++) wy[k] /= sumWy;

    /* Mirror indices for each tap. */
    const ix = new Array(6);
    const iy = new Array(6);
    for (let k = 0; k < 6; k++) {
        ix[k] = mirrorIndex(xi - 2 + k, w);
        iy[k] = mirrorIndex(yi - 2 + k, h);
    }

    let r = 0, g = 0, b = 0, a = 0;
    for (let j = 0; j < 6; j++) {
        const rowOff = iy[j] * w * C;
        const wyJ = wy[j];
        for (let i = 0; i < 6; i++) {
            const off = rowOff + ix[i] * C;
            const wt  = wx[i] * wyJ;
            r += rgba[off]     * wt;
            g += rgba[off + 1] * wt;
            b += rgba[off + 2] * wt;
            a += rgba[off + 3] * wt;
        }
    }
    dst[dstOffset]     = r;
    dst[dstOffset + 1] = g;
    dst[dstOffset + 2] = b;
    dst[dstOffset + 3] = a;
}

/**
 * Registry of resampler implementations. Keyed by string id so callers
 * can pass `{ resampler: 'lanczos3' }` from a UI dropdown.
 *
 * Default for production: 'lanczos3' (Duchon 1979, highest visual
 * fidelity). Default for preview tier: 'bilinear' (~10× faster).
 */
export const RESAMPLERS = {
    bilinear: sampleBilinear,
    bicubic:  sampleBicubic,
    lanczos3: sampleLanczos3,
};

/**
 * Returns the function for a given resampler id, or throws if unknown.
 * @param {string} id
 * @returns {Function}
 */
export function getResampler(id) {
    const fn = RESAMPLERS[id];
    if (typeof fn !== 'function') {
        throw new Error(`Unknown resampler '${id}'. Known: ${Object.keys(RESAMPLERS).join(', ')}`);
    }
    return fn;
}
